import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { resolveImg } from "@/lib/api";
import { BackHeader } from "@/components/TopBar";
import { Spinner } from "@/components/common";
import { CompatibilityBreakdown } from "@/components/CompatibilityBreakdown";
import { TrustBadges, TrustLevel } from "@/components/TrustBadges";
import { SaveButton } from "@/components/SaveButton";
import { ListingRailCard } from "@/components/ListingCard";
import { Button } from "@/components/ui/button";
import { fmtRent, fmtDate, LABELS } from "@/lib/constants";
import { toast } from "sonner";
import { MapPin, Briefcase, Wallet, Calendar, Heart, MessageCircle, Moon, Sparkles, Utensils, Cigarette } from "lucide-react";

export default function PersonDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [u, setU] = useState(null);
  const [savedIds, setSavedIds] = useState([]);

  useEffect(() => {
    api.get(`/users/${id}`).then((r) => setU(r.data)).catch(() => toast.error("Profile not found"));
    api.get("/saved/ids").then((r) => setSavedIds(r.data.ids)).catch(() => {});
  }, [id]);

  if (!u) return <div><BackHeader title="Profile" /><Spinner /></div>;
  const p = u.profile || {};

  const interest = async () => {
    try { await api.post("/interests", { target_type: "person", target_id: u.user_id }); toast.success(`Interest sent to ${p.first_name}!`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not send interest"); }
  };
  const message = async () => {
    try {
      const { data } = await api.post("/conversations", { other_user_id: u.user_id });
      navigate(`/chat/${data.conversation_id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start chat"); }
  };

  const foodLabel = LABELS.personal_food?.[p.personal_food] || LABELS.food_preference?.[p.food_preference];
  const comfy = (p.food_comfortable || []).map((c) => LABELS.food_comfortable?.[c] || c).join(" & ");
  const traits = [
    { icon: Moon, label: "Sleep", v: LABELS.sleep_time[p.sleep_time] },
    { icon: Sparkles, label: "Social", v: LABELS.social_lifestyle[p.social_lifestyle] },
    { icon: Utensils, label: "Eats", v: foodLabel },
    { icon: Utensils, label: "OK with", v: comfy },
    { icon: Briefcase, label: "Work mode", v: LABELS.work_mode?.[p.work_mode] },
    { icon: Cigarette, label: "Smoking", v: LABELS.smoking[p.smoking] },
  ].filter((t) => t.v);

  return (
    <div className="pb-28">
      <BackHeader title={p.first_name || "Profile"} right={<SaveButton itemType="person" itemId={u.user_id} savedIds={savedIds} onChange={(i, on) => setSavedIds((s) => on ? [...s, i] : s.filter((x) => x !== i))} />} />

      <div className="relative aspect-[3/4] max-h-[460px] overflow-hidden bg-secondary">
        <img src={resolveImg(p.photo_url) || "https://images.unsplash.com/photo-1625241152315-4a698f74ceb7?w=800"} alt="" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-5 text-white">
          <h1 className="font-heading text-3xl font-extrabold">{p.first_name}{p.age ? `, ${p.age}` : ""}</h1>
          <p className="flex items-center gap-1.5 text-white/90"><Briefcase className="h-4 w-4" /> {p.occupation} {p.company_college ? `· ${p.company_college}` : ""}</p>
          <p className="flex items-center gap-1.5 text-sm text-white/80"><MapPin className="h-4 w-4" /> {p.city}</p>
          <div className="mt-2"><TrustBadges verification={u.verification} showLevel level={u.trust_level} /></div>
        </div>
      </div>

      <div className="space-y-6 px-4 pt-5">
        {p.bio && <p className="leading-relaxed text-ink-soft">{p.bio}</p>}

        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-2xl bg-white p-3 shadow-soft">
            <div className="flex items-center gap-1.5 text-ink-soft"><Wallet className="h-4 w-4" /><span className="text-xs">Budget</span></div>
            <p className="mt-1 font-heading font-bold text-ink">{p.budget_min ? `${fmtRent(p.budget_min)}–${fmtRent(p.budget_max)}` : "Flexible"}</p>
          </div>
          <div className="rounded-2xl bg-white p-3 shadow-soft">
            <div className="flex items-center gap-1.5 text-ink-soft"><Calendar className="h-4 w-4" /><span className="text-xs">Move-in</span></div>
            <p className="mt-1 font-heading font-bold text-ink">{fmtDate(p.move_in_date)}</p>
          </div>
        </div>

        {p.preferred_localities?.length > 0 && (
          <div>
            <h3 className="mb-2 font-heading text-lg font-bold text-ink">Preferred areas</h3>
            <div className="flex flex-wrap gap-2">{p.preferred_localities.map((l) => <span key={l} className="rounded-full bg-brand-light px-3 py-1.5 text-sm font-semibold text-brand-dark">{l}</span>)}</div>
          </div>
        )}

        {traits.length > 0 && (
          <div>
            <h3 className="mb-2 font-heading text-lg font-bold text-ink">Lifestyle</h3>
            <div className="grid grid-cols-2 gap-3">
              {traits.map((t) => (
                <div key={t.label} className="flex items-center gap-3 rounded-2xl bg-white p-3 shadow-soft">
                  <div className="grid h-9 w-9 place-items-center rounded-xl bg-secondary"><t.icon className="h-4 w-4 text-brand" /></div>
                  <div><p className="text-xs text-ink-soft">{t.label}</p><p className="font-semibold text-ink">{t.v}</p></div>
                </div>
              ))}
            </div>
          </div>
        )}

        {u.compatibility && <CompatibilityBreakdown compatibility={u.compatibility} name={p.first_name} />}

        {u.listings?.length > 0 && (
          <div>
            <h3 className="mb-2 font-heading text-lg font-bold text-ink">{p.first_name}'s listings</h3>
            <div className="no-scrollbar -mx-4 flex gap-3 overflow-x-auto px-4 pb-2">
              {u.listings.map((l) => <ListingRailCard key={l.listing_id} listing={l} />)}
            </div>
          </div>
        )}
      </div>

      <div className="fixed bottom-0 left-1/2 flex w-full max-w-[480px] -translate-x-1/2 gap-3 border-t border-gray-100 bg-white/95 px-4 py-4 backdrop-blur-xl">
        <Button data-testid="express-interest-person" onClick={interest} variant="outline" className="h-12 flex-1 rounded-full border-brand font-bold text-brand"><Heart className="mr-1.5 h-4 w-4" /> Interested</Button>
        <Button data-testid="message-person" onClick={message} className="h-12 flex-1 rounded-full bg-brand font-bold text-white shadow-brand hover:bg-brand-dark"><MessageCircle className="mr-1.5 h-4 w-4" /> Message</Button>
      </div>
    </div>
  );
}
